(() => {
  if (customElements.get('loshen-led-guide')) return;

  function deviceView(button, focus = false) {
    const root = button.closest('[data-device-explorer]');

    root.querySelectorAll('[data-device-tab]').forEach(tab => {
      const active = tab === button;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    root.querySelectorAll('[data-device-panel]').forEach(panel => {
      panel.hidden = panel.dataset.devicePanel !== button.dataset.deviceTab;
    });

    if (focus) button.focus();
  }

  function selectColour(root, key, focus = false) {
    root.dataset.active = key;

    root.querySelectorAll('[data-spectrum-colour]').forEach(button => {
      const active = button.dataset.spectrumColour === key;
      button.setAttribute('aria-pressed', String(active));

      if (active && focus) button.focus();
    });

    root.querySelectorAll('[data-spectrum-panel]').forEach(panel => {
      panel.hidden = panel.dataset.spectrumPanel !== key;
    });

    updateSkin(root);
  }

  function filterColours(root, key) {
    root.dataset.filter = key;

    root.querySelectorAll('[data-spectrum-filter]').forEach(button => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.spectrumFilter === key)
      );
    });

    root.querySelectorAll('[data-spectrum-colour]').forEach(button => {
      button.hidden = key === 'device' && button.dataset.included !== 'true';
    });

    for (const [container, item] of [
      ['.lc-spectrum__more', '.lc-spectrum__chip'],
      ['.lc-spectrum__paths', '.lc-spectrum__path']
    ]) {
      const group = root.querySelector(container);

      if (group) {
        group.hidden = !Array.from(
          root.querySelectorAll(item)
        ).some(button => !button.hidden);
      }
    }

    const active = root.querySelector(
      '[data-spectrum-colour][aria-pressed="true"]'
    );

    if (active?.hidden) {
      selectColour(root, 'all');
    } else {
      updateSkin(root);
    }
  }

  function handleClick(event) {
    const target = event.target instanceof Element
      ? event.target
      : event.target.parentElement;

    const view = target.closest('[data-device-tab]');

    if (view) deviceView(view);

    const root = target.closest('[data-spectrum]');

    if (root) {
      const colour = target.closest('[data-spectrum-colour]');
      const filter = target.closest('[data-spectrum-filter]');

      if (colour) {
        selectColour(root, colour.dataset.spectrumColour);
      } else if (filter) {
        filterColours(root, filter.dataset.spectrumFilter);
      } else if (target.closest('[data-spectrum-reset]')) {
        selectColour(root, 'all');
      }
    }

    const summary = target.closest('summary');

    if (
      summary?.parentElement.matches('[data-led-fold]') &&
      !summary.parentElement.open
    ) {
      this.querySelectorAll('[data-led-fold][open]').forEach(fold => {
        if (fold !== summary.parentElement) fold.open = false;
      });
    }
  }

  function handleKey(event) {
    const button = event.target.closest(
      '[data-device-tab], [data-spectrum-colour]'
    );

    if (!button) return;

    const isDevice = button.matches('[data-device-tab]');

    const root = button.closest(
      isDevice ? '[data-device-explorer]' : '[data-spectrum]'
    );

    const buttons = Array.from(
      root.querySelectorAll(
        isDevice ? '[data-device-tab]' : '[data-spectrum-colour]'
      )
    ).filter(item => !item.hidden);

    let index = buttons.indexOf(button);

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      index = (index + 1) % buttons.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      index = (index + buttons.length - 1) % buttons.length;
    } else if (event.key === 'Home') {
      index = 0;
    } else if (event.key === 'End') {
      index = buttons.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    if (isDevice) {
      deviceView(buttons[index], true);
    } else {
      selectColour(root, buttons[index].dataset.spectrumColour, true);
    }
  }

  const spec = {
    purple: ['#9773b0', 235, '440'],
    blue: ['#5589bf', 235, '415–465'],
    cyan: ['#57a5a9', 235, '485'],
    green: ['#779966', 282, '520'],
    amber: ['#b99138', 282, '570'],
    yellow: ['#b89b36', 282, '580'],
    orange: ['#bf8241', 366, '600'],
    red: ['#b9514d', 366, '633–650'],
    nir: ['#8c7966', 440, '830–850'],
    deep_nir: ['#746b62', 440, '1064–1072'],
    white: ['#aaa294', 366, 'mix']
  };

  function updateSkin(root) {
    const fig = root.querySelector('.lc-skin');

    if (!fig) return;

    const fr = root.closest('loshen-led-guide').dataset.locale === 'fr';
    const key = root.dataset.active || 'all';
    const t = (a, b) => fr ? b : a;

    const compare = fig.querySelector('[data-skin-comparison]');
    const single = fig.querySelector('[data-skin-single]');
    const white = fig.querySelector('[data-skin-white]');

    compare.style.display = key === 'all' ? '' : 'none';
    single.style.display = key === 'all' || key === 'white' ? 'none' : '';
    white.style.display = key === 'white' ? '' : 'none';

    let title = '';
    let note = '';

    if (key === 'all') {
      compare.querySelectorAll('[data-skin-ray]').forEach(ray => {
        const button = root.querySelector(
          '[data-spectrum-colour="' + ray.dataset.skinRay + '"]'
        );

        ray.style.display =
          root.dataset.filter !== 'device' ||
          button?.dataset.included === 'true'
            ? ''
            : 'none';
      });

      title = t(
        'Every colour, beneath the surface.',
        'Toutes les couleurs, sous la surface.'
      );

      note = t(
        'Choose a colour to explore its illustrative path. Shorter visible wavelengths are generally more superficial; red and near-infrared can travel further.',
        'Choisissez une couleur pour explorer son parcours indicatif. Les courtes longueurs d’onde visibles restent généralement plus superficielles; le rouge et le proche infrarouge peuvent aller plus loin.'
      );

      if (root.dataset.filter === 'device') {
        title = t(
          'The light modes in this device.',
          'Les modes lumineux de cet appareil.'
        );
      }
    } else {
      const panel = root.querySelector(
        '[data-spectrum-panel="' + key + '"]'
      );

      const label = panel.querySelector('h4').textContent.trim();
      const [colour, end, wave] = spec[key];

      const path = fig.querySelector('[data-skin-single-path]');

      path.setAttribute(
        'd',
        `M390 75H430L${430 + (end - 75) * .11} ${end}H${390 - (end - 75) * .11}Z`
      );

      fig.querySelectorAll('[data-led-skin-gradient] stop').forEach(stop => {
        stop.setAttribute('stop-color', colour);
      });

      const line = fig.querySelector('[data-skin-nir-line]');

      line.style.display = ['nir', 'deep_nir'].includes(key) ? '' : 'none';
      line.setAttribute('d', `M410 80V${end - 73}`);

      fig.querySelector('[data-skin-single-label]').textContent =
        label + ' · ' + wave + ' nm';

      if (key === 'white') {
        title = t(
          'White · a blend of visible wavelengths',
          'Blanc · un mélange de longueurs d’onde visibles'
        );

        note = t(
          'The coloured paths show how a mixture can spread through different layers. This is a conceptual example: the exact paths depend on the device’s spectrum.',
          'Les parcours colorés montrent qu’un mélange se répartit dans différentes couches. Cet exemple est conceptuel : les parcours exacts dépendent du spectre de l’appareil.'
        );
      } else if (end === 235) {
        title = label + ' · ' + t(
          'mostly superficial',
          'surtout en surface'
        );

        note = t(
          'An illustrative path for ' + wave + ' nm. Much of this light is attenuated in the shallower skin layers; some may travel further.',
          'Parcours indicatif pour ' + wave + ' nm. Cette lumière s’atténue surtout dans les couches superficielles; une partie peut aller plus loin.'
        );
      } else if (end === 282) {
        title = label + ' · ' + t(
          'superficial skin & upper dermis',
          'peau superficielle et haut du derme'
        );

        note = t(
          'An illustrative path for ' + wave + ' nm. This broad region is not a measured boundary or a promised treatment depth.',
          'Parcours indicatif pour ' + wave + ' nm. Cette zone générale n’est ni une frontière mesurée ni une profondeur de traitement garantie.'
        );
      } else if (end === 366) {
        title = label + ' · ' + t(
          'can reach into the dermis',
          'peut atteindre le derme'
        );

        note = t(
          'An illustrative path for ' + wave + ' nm. Light passes through the epidermis, then continues into the layer containing collagen and elastin.',
          'Parcours indicatif pour ' + wave + ' nm. La lumière traverse l’épiderme, puis se propage dans la couche contenant le collagène et l’élastine.'
        );
      } else {
        title = label + ' · ' + t(
          'can reach deeper tissue',
          'peut atteindre des tissus plus profonds'
        );

        note = t(
          'An illustrative path for ' + wave + ' nm. Some light may travel beneath the dermis. Taupe represents invisible light; a longer wavelength does not automatically mean greater penetration.',
          'Parcours indicatif pour ' + wave + ' nm. Une partie peut dépasser le derme. Le taupe représente une lumière invisible; une longueur d’onde plus élevée ne signifie pas automatiquement une pénétration supérieure.'
        );
      }
    }

    fig.querySelector('[data-skin-status]').textContent = title;
    fig.querySelector('[data-skin-note]').textContent = note;

    fig.querySelector('desc').textContent =
      title + ' ' + note + ' ' + t(
        'Schematic, not to scale. Actual penetration depends on wavelength, skin and device.',
        'Schéma non à l’échelle. La pénétration réelle dépend de la longueur d’onde, de la peau et de l’appareil.'
      );
  }

  class LoshenLedGuide extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.load(this.controller.signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load(signal) {
      try {
        if (!this.dataset.ready) {
          const response = await fetch(this.dataset.contentUrl, {
            signal,
            credentials: 'omit'
          });

          if (!response.ok) {
            throw new Error('LED content: ' + response.status);
          }

          const content = await response.json();

          if (signal.aborted || !this.isConnected) return;

          if (typeof content.html !== 'string') {
            throw new Error('Invalid LED content');
          }

          const template = document.createElement('template');
          template.innerHTML = content.html;

          const prefix = this.id + '-';
          const ids = new Map();

          template.content.querySelectorAll('[id]').forEach(node => {
            const old = node.id;

            ids.set(old, prefix + old);
            node.id = prefix + old;

            if (old === 'skin-selected') {
              node.setAttribute('data-led-skin-gradient', '');
            }
          });

          template.content.querySelectorAll('*').forEach(node => {
            for (const name of [
              'aria-labelledby',
              'aria-describedby',
              'aria-controls',
              'for'
            ]) {
              if (node.hasAttribute(name)) {
                node.setAttribute(
                  name,
                  node.getAttribute(name)
                    .split(/\s+/)
                    .map(id => ids.get(id) || id)
                    .join(' ')
                );
              }
            }

            for (const attribute of Array.from(node.attributes)) {
              if (attribute.value.includes('url(#')) {
                node.setAttribute(
                  attribute.name,
                  attribute.value.replace(
                    /url\(#([^)]+)\)/g,
                    (_, id) => 'url(#' + (ids.get(id) || id) + ')'
                  )
                );
              }
            }

            if (node.matches('[data-led-fold]')) {
              node.setAttribute('name', prefix + 'folds');
            }
          });

          const assetBase = new URL(
            '.',
            new URL(this.dataset.contentUrl, window.location.href)
          );

          template.content.querySelectorAll('[data-led-asset]').forEach(img => {
            img.src = new URL(img.dataset.ledAsset, assetBase).href;
            img.loading = 'lazy';
            img.decoding = 'async';
          });

          template.content.querySelectorAll('a[href]').forEach(link => {
            const href = link.getAttribute('href');

            if (href.startsWith('#') && ids.has(href.slice(1))) {
              link.href = '#' + ids.get(href.slice(1));
            } else {
              const url = new URL(href, window.location.origin);

              if (
                ['www.loshen.ca', 'loshen.ca'].includes(url.hostname) &&
                url.pathname.startsWith('/products/')
              ) {
                link.href =
                  this.dataset.shopRoot.replace(/\/$/, '') +
                  url.pathname +
                  url.search +
                  url.hash;
              }
            }
          });

          this.replaceChildren(template.content);
          this.dataset.ready = 'true';
        }

        if (signal.aborted) return;

        this.addEventListener('click', handleClick, { signal });
        this.addEventListener('keydown', handleKey, { signal });

        this.querySelectorAll('[data-spectrum]').forEach(updateSkin);
        this.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.warn('Loshen LED section could not load.', error);

        if (window.Shopify?.designMode) {
          this.hidden = false;

          this.textContent = this.dataset.locale === 'fr'
            ? 'Loshen DEL : téléversez les fichiers du dossier assets, puis actualisez cet aperçu.'
            : 'Loshen LED: upload the files from the assets folder, then refresh this preview.';
        }
      }
    }
  }

  customElements.define('loshen-led-guide', LoshenLedGuide);
})();
